/**
 * WUA Fast Match — client API helpers.
 *
 * Thin wrapper around `/api/beta/course-fit/*` that guarantees:
 *   • the anonymous accessToken lives in sessionStorage (never
 *     localStorage — resume across tabs is deliberately out of scope)
 *   • every state-mutating call is guarded by a monotonically increasing
 *     `clientSequence` (double-submit / stale-click protection)
 *   • errors surface as { code, message } shapes matching Stage 02's
 *     `CourseFitError.to_dict()` — the UI reads `code` for branching.
 *
 * This module has NO React dependencies and can be unit-tested in
 * isolation. All React state lives one layer up in useCourseFitSession.
 */
import { api } from "@/lib/api";
import { getCookieConsent } from "@/components/app/CookieBanner";

const SS_SESSION_KEY = "wua.courseFit.session";  // sessionStorage
const SEQ_KEY = "wua.courseFit.seq";

export function loadStoredSession() {
  try {
    const raw = sessionStorage.getItem(SS_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function storeSession(session) {
  try {
    sessionStorage.setItem(SS_SESSION_KEY, JSON.stringify(session));
  } catch { /* private mode / storage disabled — safe to ignore */ }
}

export function clearStoredSession() {
  try {
    sessionStorage.removeItem(SS_SESSION_KEY);
    sessionStorage.removeItem(SEQ_KEY);
  } catch { /* ignore */ }
}

export function nextSequence() {
  const cur = Number(sessionStorage.getItem(SEQ_KEY) || 0);
  const nxt = cur + 1;
  try { sessionStorage.setItem(SEQ_KEY, String(nxt)); } catch { /* ignore */ }
  return nxt;
}

function unwrap(err) {
  const d = err?.response?.data;
  if (d && typeof d === "object") {
    // FastAPI wraps our to_dict() as { detail: { code, message, ... } }
    return d.detail || d;
  }
  return { code: "network_error", message: err?.message || "Network error" };
}

export async function fetchResumeState(sessionId, accessToken) {
  try {
    const r = await api.get(`/beta/course-fit/session/${sessionId}/resume`, {
      headers: accessToken ? { "X-Course-Fit-Access-Token": accessToken } : {},
    });
    return { ok: true, data: r.data };
  } catch (err) {
    return { ok: false, error: unwrap(err) };
  }
}

export async function fetchResult(sessionId, accessToken) {
  try {
    const r = await api.get(`/beta/course-fit/session/${sessionId}/result`, {
      headers: accessToken ? { "X-Course-Fit-Access-Token": accessToken } : {},
    });
    return { ok: true, data: r.data };
  } catch (err) {
    return { ok: false, error: unwrap(err) };
  }
}

export async function fetchFlag() {
  const r = await api.get("/beta/course-fit/flag");
  return r.data;
}

export async function fetchRelease() {
  const r = await api.get("/beta/course-fit/release");
  return r.data;
}

export async function createSession() {
  const r = await api.post("/beta/course-fit/session");
  const session = { sessionId: r.data.sessionId, accessToken: r.data.accessToken };
  storeSession(session);
  try { sessionStorage.setItem(SEQ_KEY, "0"); } catch { /* ignore */ }
  return r.data;
}

export async function saveAnswer(sessionId, questionId, optionIds) {
  try {
    const r = await api.post(`/beta/course-fit/session/${sessionId}/answer`, {
      questionId, optionIds, clientSequence: nextSequence(),
    });
    return { ok: true, data: r.data };
  } catch (err) {
    return { ok: false, error: unwrap(err) };
  }
}

export async function buildPreliminary(sessionId) {
  try {
    const r = await api.post(`/beta/course-fit/session/${sessionId}/preliminary`);
    return { ok: true, data: r.data };
  } catch (err) {
    return { ok: false, error: unwrap(err) };
  }
}

export async function completeSession(sessionId, choice) {
  try {
    const r = await api.post(`/beta/course-fit/session/${sessionId}/complete`, {
      choice, clientSequence: nextSequence(),
    });
    return { ok: true, data: r.data };
  } catch (err) {
    return { ok: false, error: unwrap(err) };
  }
}


// ---------------------------------------------------------------------
// Stage 05 — feedback + client-side analytics
// ---------------------------------------------------------------------
//
// Client-emittable event names must match `beta_ops.CLIENT_EMITTABLE_EVENTS`
// server-side. Anything else is rejected with `unknown_client_event`.
export const FAST_MATCH_CLIENT_EVENTS = Object.freeze({
  SCREEN_VIEWED: "course_fit_screen_viewed",
  ANSWER_SAVED:  "course_fit_answer_saved",
  SCREEN_ERROR:  "course_fit_screen_error",
  RESULT_OPENED: "course_fit_result_opened",
});

/**
 * Fire a client-side Fast Match analytics event, gated by the WUA
 * cookie consent stored in localStorage. Never surfaces errors to the
 * caller — analytics must not break the interview flow.
 */
export function trackCourseFitEvent(sessionId, accessToken, event, properties = {}) {
  try {
    if (getCookieConsent() !== "accepted") return;
    if (!sessionId || !accessToken || !event) return;
    api.post(`/beta/course-fit/session/${sessionId}/event`,
             { event, properties: properties || {} },
             { headers: { "X-Course-Fit-Access-Token": accessToken } })
       .catch(() => { /* swallow — instrumentation must not break UX */ });
  } catch { /* ignore */ }
}

export async function submitFeedback(sessionId, accessToken, { usefulness, reasons, comment }) {
  try {
    const r = await api.post(
      `/beta/course-fit/session/${sessionId}/feedback`,
      { usefulness, reasons: reasons || [], comment: comment || "" },
      { headers: accessToken ? { "X-Course-Fit-Access-Token": accessToken } : {} },
    );
    return { ok: true, data: r.data };
  } catch (err) {
    return { ok: false, error: unwrap(err) };
  }
}

export async function fetchBetaConfig() {
  try {
    const r = await api.get("/beta/course-fit/beta/config");
    return { ok: true, data: r.data };
  } catch (err) {
    return { ok: false, error: unwrap(err) };
  }
}

// ---------------------------------------------------------------------
// Saved results (signed-in owners), interest plan, course matches,
// "stay within easy reach" preference.
// ---------------------------------------------------------------------
async function call(fn) {
  try { const r = await fn(); return { ok: true, data: r.data }; }
  catch (err) { return { ok: false, status: err?.response?.status, error: unwrap(err) }; }
}
const tokenHeader = (t) => (t ? { headers: { "X-Course-Fit-Access-Token": t } } : {});

export const fetchMyResults = () => call(() => api.get("/beta/course-fit/me/results"));
export const fetchFamilyPreviewFlag = () => call(() => api.get("/beta/course-fit/family-preview/flag"));
export const fetchFamilyPreview = (id, token, chosen = []) => {
  const q = new URLSearchParams(chosen.map((c) => ["chosen", c])).toString();
  return call(() => api.get(`/beta/course-fit/session/${id}/family-preview${q ? `?${q}` : ""}`, tokenHeader(token)));
};
export const saveFamilyPreview = (id, token, body) => call(() => api.put(`/beta/course-fit/session/${id}/family-preview`, body, tokenHeader(token)));
export const fetchMyResult = (id) => call(() => api.get(`/beta/course-fit/me/results/${id}`));
export const fetchMyAnswers = (id) => call(() => api.get(`/beta/course-fit/me/results/${id}/answers`));
export const attachSession = (id, token) => call(() => api.post(`/beta/course-fit/session/${id}/attach`, {}, tokenHeader(token)));
export const fetchPlan = (id, token) => call(() => api.get(`/beta/course-fit/session/${id}/plan`, tokenHeader(token)));
export const savePlan = (id, token, plan) => call(() => api.put(`/beta/course-fit/session/${id}/plan`, plan, tokenHeader(token)));
export const fetchEditedFrom = (id, token) => call(() => api.get(`/beta/course-fit/session/${id}/edited-from`, tokenHeader(token)));
export const linkEditedFrom = (id, token, fromSessionId) => call(() => api.put(`/beta/course-fit/session/${id}/edited-from`, { fromSessionId }, tokenHeader(token)));
export const fetchSubjects = () => call(() => api.get("/beta/course-fit/subjects"));
export const fetchDeclared = (id, token) => call(() => api.get(`/beta/course-fit/session/${id}/declared-interests`, tokenHeader(token)));
export const saveDeclared = (id, token, body) => call(() => api.put(`/beta/course-fit/session/${id}/declared-interests`, body, tokenHeader(token)));
export const fetchCourseMatches = (subject, cities = []) => {
  const q = new URLSearchParams([["subject", subject], ["limit", "6"], ...cities.map((c) => ["city", c])]).toString();
  return call(() => api.get(`/beta/course-fit/course-matches?${q}`));
};
export const fetchProximity = () => call(() => api.get("/me/proximity-preference"));
export const saveProximity = (body) => call(() => api.put("/me/proximity-preference", body));
export const clearProximity = () => call(() => api.delete("/me/proximity-preference"));
